import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const INIT = {
  name: '', description: '', customer_id: '', opportunity_id: '',
  status: 'planning', priority: 'medium',
  start_date: '', due_date: '', budget: '', hourly_rate: '',
  manager_id: '', notes: '',
};
const LIMIT = 15;
const STATUSES = ['planning', 'active', 'on_hold', 'completed', 'cancelled'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const statusColor = {
  planning: 'info', active: 'active', on_hold: 'warning',
  completed: 'primary', cancelled: 'inactive',
};
const priorityColor = { low: 'info', medium: 'warning', high: 'primary', urgent: 'inactive' };
const fmt = (v) => parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

export default function Projects({ setTitle }) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const [form, setForm]         = useState(INIT);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [opps, setOpps]         = useState([]);
  const [users, setUsers]       = useState([]);

  useEffect(() => { setTitle('Projects'); }, [setTitle]);
  useEffect(() => {
    api.get('/customers/all').then(r => setCustomers(r.data)).catch(() => {});
    api.get('/opportunities?limit=200').then(r => setOpps(r.data.data || [])).catch(() => {});
    api.get('/auth/users').then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ search, page, limit: LIMIT });
      if (filterStatus) params.set('status', filterStatus);
      const { data } = await api.get(`/projects?${params}`);
      setItems(data.data || []); setTotal(data.total || 0);
    } catch { setItems([]); } finally { setLoading(false); }
  }, [search, page, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm(INIT); setEditId(null); setModal(true); };
  const openEdit = (item) => {
    setForm({
      name: item.name, description: item.description || '',
      customer_id: item.customer_id || '', opportunity_id: item.opportunity_id || '',
      status: item.status, priority: item.priority,
      start_date: item.start_date ? item.start_date.split('T')[0] : '',
      due_date: item.due_date ? item.due_date.split('T')[0] : '',
      budget: item.budget || '', hourly_rate: item.hourly_rate || '',
      manager_id: item.manager_id || '', notes: item.notes || '',
    });
    setEditId(item.id); setModal(true);
  };

  const openDetail = async (item) => {
    try {
      const { data } = await api.get(`/projects/${item.id}`);
      setDetailModal(data);
    } catch { alert('Error loading project details'); }
  };

  const closeModal = () => setModal(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) return alert('Project name is required');
    setSaving(true);
    try {
      if (editId) await api.put(`/projects/${editId}`, form);
      else        await api.post('/projects', form);
      closeModal(); load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving project');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete project "${name}"? This will also delete all tasks and time logs.`)) return;
    try { await api.delete(`/projects/${id}`); load(); }
    catch (err) { alert(err.response?.data?.error || 'Error deleting'); }
  };

  const handleGenerateInvoice = async (projectId, projectName) => {
    if (!window.confirm(`Generate invoice from uninvoiced billable hours for "${projectName}"?`)) return;
    setInvoicing(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/invoice`);
      alert(`Invoice ${data.invoice_number} created for $${fmt(data.amount)}`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error generating invoice');
    } finally { setInvoicing(false); }
  };

  const progress = (item) => {
    if (!item.task_count) return 0;
    return Math.round((item.done_count / item.task_count) * 100);
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Projects</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Project
        </button>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input placeholder="Search project or customer…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <select className="form-control" style={{ width: 160 }} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
          </select>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} projects</span>
        </div>

        <div className="table-wrap">
          {loading ? <div className="loading">Loading…</div> : (
            <table>
              <thead>
                <tr>
                  <th>#</th><th>Name</th><th>Customer</th><th>Status</th><th>Priority</th>
                  <th>Progress</th><th>Hours</th><th>Billed</th><th>Due Date</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={10} className="empty">No projects found</td></tr>
                ) : items.map((item, i) => {
                  const pct = progress(item);
                  return (
                    <tr key={item.id}>
                      <td className="text-muted">{(page - 1) * LIMIT + i + 1}</td>
                      <td>
                        <button className="btn-link" onClick={() => openDetail(item)} style={{ fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', padding: 0 }}>
                          {item.name}
                        </button>
                      </td>
                      <td>{item.customer_name || '—'}</td>
                      <td><span className={`badge badge-${statusColor[item.status]}`} style={{ textTransform: 'capitalize' }}>{item.status.replace('_', ' ')}</span></td>
                      <td><span className={`badge badge-${priorityColor[item.priority]}`} style={{ textTransform: 'capitalize' }}>{item.priority}</span></td>
                      <td style={{ minWidth: 120 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? 'var(--success)' : 'var(--primary)', borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{item.done_count}/{item.task_count}</span>
                        </div>
                      </td>
                      <td>{parseFloat(item.logged_hours || 0).toFixed(1)}h</td>
                      <td>${fmt(item.billed_amount)}</td>
                      <td>{item.due_date ? item.due_date.split('T')[0] : '—'}</td>
                      <td>
                        <div className="actions">
                          <button className="btn-icon" title="Generate Invoice" onClick={() => handleGenerateInvoice(item.id, item.name)} disabled={invoicing}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/></svg>
                          </button>
                          <button className="btn-icon" title="Edit" onClick={() => openEdit(item)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id, item.name)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
            <div className="pagination-btns">
              <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detailModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDetailModal(null)}>
          <div className="modal" style={{ maxWidth: 620 }}>
            <div className="modal-header">
              <h2>{detailModal.name}</h2>
              <button className="modal-close" onClick={() => setDetailModal(null)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                {[
                  ['Customer', detailModal.customer_name || '—'],
                  ['Manager', detailModal.manager_name || '—'],
                  ['Status', detailModal.status],
                  ['Priority', detailModal.priority],
                  ['Start Date', detailModal.start_date?.split('T')[0] || '—'],
                  ['Due Date', detailModal.due_date?.split('T')[0] || '—'],
                  ['Budget', `$${fmt(detailModal.budget)}`],
                  ['Hourly Rate', `$${fmt(detailModal.hourly_rate)}/hr`],
                ].map(([label, val]) => (
                  <div key={label} style={{ background: 'var(--bg-secondary)', padding: '8px 12px', borderRadius: 6 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{val}</div>
                  </div>
                ))}
              </div>

              {detailModal.description && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Description</div>
                  <p style={{ color: 'var(--text-muted)', margin: 0 }}>{detailModal.description}</p>
                </div>
              )}

              {detailModal.milestones?.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Milestones</div>
                  {detailModal.milestones.map(m => (
                    <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                      <span>{m.name}</span>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {m.due_date && <span className="text-muted" style={{ fontSize: 12 }}>{m.due_date.split('T')[0]}</span>}
                        <span className={`badge badge-${m.status === 'completed' ? 'active' : 'info'}`}>{m.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDetailModal(null)}>Close</button>
              <button className="btn btn-primary" onClick={() => { setDetailModal(null); openEdit(detailModal); }}>Edit Project</button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal" style={{ maxWidth: 680 }}>
            <div className="modal-header">
              <h2>{editId ? 'Edit Project' : 'New Project'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full">
                  <label>Project Name *</label>
                  <input className="form-control" value={form.name} onChange={e => f('name', e.target.value)} placeholder="Project name" />
                </div>
                <div className="form-group">
                  <label>Customer</label>
                  <select className="form-control" value={form.customer_id} onChange={e => f('customer_id', e.target.value)}>
                    <option value="">— None —</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Linked Opportunity</label>
                  <select className="form-control" value={form.opportunity_id} onChange={e => f('opportunity_id', e.target.value)}>
                    <option value="">— None —</option>
                    {opps.map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-control" value={form.status} onChange={e => f('status', e.target.value)}>
                    {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select className="form-control" value={form.priority} onChange={e => f('priority', e.target.value)}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Start Date</label>
                  <input type="date" className="form-control" value={form.start_date} onChange={e => f('start_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <input type="date" className="form-control" value={form.due_date} onChange={e => f('due_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Budget ($)</label>
                  <input type="number" className="form-control" value={form.budget} onChange={e => f('budget', e.target.value)} placeholder="0.00" min="0" step="0.01" />
                </div>
                <div className="form-group">
                  <label>Hourly Rate ($/hr)</label>
                  <input type="number" className="form-control" value={form.hourly_rate} onChange={e => f('hourly_rate', e.target.value)} placeholder="0.00" min="0" step="0.01" />
                </div>
                <div className="form-group">
                  <label>Project Manager</label>
                  <select className="form-control" value={form.manager_id} onChange={e => f('manager_id', e.target.value)}>
                    <option value="">— None —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="form-group full">
                  <label>Description</label>
                  <textarea className="form-control" rows={3} value={form.description} onChange={e => f('description', e.target.value)} placeholder="Project description…" />
                </div>
                <div className="form-group full">
                  <label>Notes</label>
                  <textarea className="form-control" rows={2} value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Internal notes…" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update Project' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
